import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Loader2, X } from 'lucide-react';
import type {
  BackofficeNavigationChildItem,
  BackofficeNavigationItem,
  BackofficeNavigationItemId,
  BackofficeNavigationTopLevelItemId,
} from '@/components/layout/backoffice-navigation-items';
import type { BackofficeNavigationPreferenceValue } from '@/preferences';

export interface NavigationSettingsModalProps {
  defaultValue: BackofficeNavigationPreferenceValue;
  error?: string | null;
  initialValue: BackofficeNavigationPreferenceValue;
  items: BackofficeNavigationItem[];
  onClose: () => void;
  onSave: (value: BackofficeNavigationPreferenceValue) => Promise<void> | void;
  submitting?: boolean;
}

export function NavigationSettingsModal({
  defaultValue,
  error = null,
  initialValue,
  items,
  onClose,
  onSave,
  submitting = false,
}: NavigationSettingsModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const [draft, setDraft] = useState<BackofficeNavigationPreferenceValue>(initialValue);
  const isDirty = JSON.stringify(draft) !== JSON.stringify(initialValue);
  const isDefault = JSON.stringify(draft) === JSON.stringify(defaultValue);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !submitting) {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, submitting]);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setDraft((current) => {
      const oldIndex = current.orderedItemIds.indexOf(
        active.id as BackofficeNavigationTopLevelItemId
      );
      const newIndex = current.orderedItemIds.indexOf(
        over.id as BackofficeNavigationTopLevelItemId
      );

      if (oldIndex === -1 || newIndex === -1) {
        return current;
      }

      return {
        ...current,
        orderedItemIds: arrayMove(current.orderedItemIds, oldIndex, newIndex),
      };
    });
  }

  function handleToggleTopLevel(item: BackofficeNavigationItem) {
    setDraft((current) => {
      const isShown = !current.hiddenItemIds.includes(item.id);
      const itemIds = [item.id, ...(item.children?.map((child) => child.id) ?? [])];
      const nextHiddenItemIds = isShown
        ? Array.from(new Set([...current.hiddenItemIds, ...itemIds]))
        : current.hiddenItemIds.filter((id) => id !== item.id);

      return {
        ...current,
        hiddenItemIds: nextHiddenItemIds,
      };
    });
  }

  function handleToggleChild(itemId: BackofficeNavigationItemId) {
    setDraft((current) => {
      const nextHiddenItemIds = current.hiddenItemIds.includes(itemId)
        ? current.hiddenItemIds.filter((id) => id !== itemId)
        : [...current.hiddenItemIds, itemId];

      return {
        ...current,
        hiddenItemIds: nextHiddenItemIds,
      };
    });
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/45 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !submitting) {
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="navigation-settings-title"
        className="flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-neutral-200 px-6 py-4">
          <div className="min-w-0">
            <h2
              id="navigation-settings-title"
              className="font-inter-tight text-xl font-semibold text-neutral-900"
            >
              Navigation menu
            </h2>
            <p className="mt-1 text-body text-neutral-600">
              Drag items to reorder them and choose which links appear in the backoffice menu.
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md p-2 text-neutral-500 hover:bg-backoffice-50 hover:text-neutral-900 disabled:cursor-not-allowed disabled:text-neutral-300"
            aria-label="Close"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={draft.orderedItemIds} strategy={rectSortingStrategy}>
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-6 py-5">
              {draft.orderedItemIds.map((itemId) => {
                const item = items.find((candidate) => candidate.id === itemId);

                if (!item) {
                  return null;
                }

                return (
                  <SortableNavigationItemGroup
                    key={item.id}
                    item={item}
                    hiddenItemIds={draft.hiddenItemIds}
                    onToggleTopLevel={() => handleToggleTopLevel(item)}
                    onToggleChild={handleToggleChild}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>

        {error ? <p className="px-6 pb-2 text-small text-red-700">{error}</p> : null}

        <div className="flex items-center justify-between gap-3 border-t border-neutral-200 px-6 py-4">
          <button
            type="button"
            onClick={() => setDraft(defaultValue)}
            disabled={submitting || isDefault}
            className="rounded-lg border border-neutral-200 px-4 py-2 text-small font-semibold text-neutral-700 hover:bg-backoffice-50 disabled:cursor-not-allowed disabled:text-neutral-300"
          >
            Reset to default
          </button>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => void onSave(draft)}
              disabled={submitting || !isDirty}
              className="focus-ring inline-flex items-center rounded-lg bg-backoffice-500 px-4 py-2 text-small font-semibold text-white hover:bg-backoffice-600 disabled:cursor-not-allowed disabled:bg-backoffice-300"
            >
              {submitting ? (
                <span className="inline-flex items-center whitespace-nowrap">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  &nbsp;Save
                </span>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

interface SortableNavigationItemGroupProps {
  hiddenItemIds: BackofficeNavigationItemId[];
  item: BackofficeNavigationItem;
  onToggleChild: (itemId: BackofficeNavigationItemId) => void;
  onToggleTopLevel: () => void;
}

function SortableNavigationItemGroup({
  hiddenItemIds,
  item,
  onToggleChild,
  onToggleTopLevel,
}: SortableNavigationItemGroupProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const checked = !hiddenItemIds.includes(item.id);

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={[
        'rounded-md border border-neutral-200 px-3 py-3',
        checked ? 'bg-white text-neutral-900' : 'bg-neutral-50 text-neutral-500',
        isDragging ? 'shadow-md ring-2 ring-backoffice-200' : '',
      ].join(' ')}
    >
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          className="focus-ring inline-flex h-8 w-8 shrink-0 cursor-grab items-center justify-center rounded-md text-neutral-400 hover:bg-backoffice-50 hover:text-neutral-700 active:cursor-grabbing"
          aria-label={`Move ${item.label}`}
          title="Move"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" aria-hidden="true" />
        </button>
        <span className="min-w-0 flex-1 text-body font-semibold">{item.label}</span>
        <label className="inline-flex items-center gap-2 text-small font-medium text-neutral-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-neutral-300 text-backoffice-600"
            aria-label={`Show ${item.label}`}
            checked={checked}
            onChange={onToggleTopLevel}
          />
          Show
        </label>
      </div>
      {item.children?.map((child) => (
        <NavigationChildRow
          key={child.id}
          child={child}
          checked={!hiddenItemIds.includes(child.id)}
          disabled={!checked}
          onToggle={() => onToggleChild(child.id)}
        />
      ))}
    </div>
  );
}

interface NavigationChildRowProps {
  checked: boolean;
  child: BackofficeNavigationChildItem;
  disabled: boolean;
  onToggle: () => void;
}

function NavigationChildRow({ checked, child, disabled, onToggle }: NavigationChildRowProps) {
  return (
    <div className="ml-11 flex items-center gap-3 border-l border-neutral-200 pl-3">
      <span className="min-w-0 flex-1 text-small font-medium">{child.label}</span>
      <label className="inline-flex items-center gap-2 text-small font-medium text-neutral-700">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-neutral-300 text-backoffice-600 disabled:cursor-not-allowed"
          aria-label={`Show ${child.label}`}
          checked={checked}
          disabled={disabled}
          onChange={onToggle}
        />
        Show
      </label>
    </div>
  );
}
